import { Injectable } from '@angular/core';
import { catchError, Observable, BehaviorSubject, from, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { handleErrorFunction } from '../shared/helpers/handleError';
import { ToastService } from './toast.service';
import { EntraService } from './entra.service';
import { AssetsDataService } from './assets-data.service';
import { GetLaydownLogsResponse } from '../models/logs/laydown-space/GetLaydownLogResponse';

import { LaydownLogRow } from '../models/logs/laydown-space/LaydownLogRow';
import { ApiLaydownLogRow } from '../models/logs/laydown-space/ApiLaydownLogRow';
import { LaydownLogSearch } from '../models/logs/laydown-space/LaydownLogSearch';

import {
  LoadingText,
  ToastText,
} from '../models/assets-data/UiConfig.interface';
import { SelectedLocation } from '../models/selectedLocation.interface';
import { OccupiedLaydownSpaces } from '../models/logs/laydown-space/OccupiedLaydownSpaces.interface';

/**
 * LaydownLogService handles CRUD operations for laydown space logs.
 * It uses HTTP requests to communicate with the backend API and manages
 * authorization through Entra.
 *
 * The service also manages several BehaviorSubjects which track the current state of various
 * data points related to laydown logs and loading states. These BehaviorSubjects
 * are updated when HTTP requests return data, allowing subscribed components to automatically
 * update with new information as soon as it is fetched or updated.
 *
 * The BehaviorSubjects are:
 * - `fetchedLaydownLogs`: Holds the laydown logs fetched from the backend, updated when logs are retrieved.
 * - `dataLoading`: Tracks the loading state, allowing the UI to show loading indicators while data is being fetched or submitted.
 * - `laydownLogDataSubject`: Holds the selected laydown log data, updated when a log is selected.
 * - `dateFriendlyLaydownLogDataSubject`: Holds the date-formatted laydown log data for display purposes.
 */

@Injectable({
  providedIn: 'root',
})
export class LaydownLogService {
  private _httpClient: HttpClient;
  private _toastService: ToastService;
  private _entraService: EntraService;
  private _assetsDataService: AssetsDataService;
  private _accessToken: string | null = null;

  public loadingText: LoadingText;
  public toastText: ToastText;

  // Default filter params used by both table and form components
  public initialFilterColumnParams: { [key: string]: string | null } = {
    dateOut: null,
  };

  public dataLoading = new BehaviorSubject<{
    isLoading: boolean;
    message: string;
  }>({
    isLoading: false,
    message: '',
  });
  public dataLoading$ = this.dataLoading.asObservable();
  private dataLoadingDefault = { isLoading: false, message: '' };

  public fetchedLaydownLogs = new BehaviorSubject<GetLaydownLogsResponse>(
    new GetLaydownLogsResponse()
  );
  public laydownLogDataSubject = new BehaviorSubject<ApiLaydownLogRow | null>(
    null
  );
  public logData$ = this.laydownLogDataSubject.asObservable();

  public dateFriendlyLaydownLogDataSubject =
    new BehaviorSubject<LaydownLogRow | null>(null);
  public dateFriendlyLogData$ =
    this.dateFriendlyLaydownLogDataSubject.asObservable();

  public OccupiedLaydownSpaces = new BehaviorSubject<OccupiedLaydownSpaces>({
    spaceA: [],
    spaceB: [],
  });
  public OccupiedLaydownSpaces$ = this.OccupiedLaydownSpaces.asObservable();

  constructor(
    http: HttpClient,
    toastService: ToastService,
    entraService: EntraService,
    assetsDataService: AssetsDataService
  ) {
    this._httpClient = http;
    this._toastService = toastService;
    this._entraService = entraService;
    this._assetsDataService = assetsDataService;

    const uiConfigData = this._assetsDataService.GetUiConfigData();
    this.loadingText = uiConfigData.loadingText;
    this.toastText = uiConfigData.toastText;
  }

  public setLogRowData(logRowData: ApiLaydownLogRow): void {
    this.laydownLogDataSubject.next(logRowData);
  }

  public getLogRowDataSubject(): BehaviorSubject<ApiLaydownLogRow | null> {
    return this.laydownLogDataSubject;
  }

  public setDateFriendlyLogRowData(logRowData: LaydownLogRow): void {
    this.dateFriendlyLaydownLogDataSubject.next(logRowData);
  }

  public getDateFriendlyLogRowDataSubject(): BehaviorSubject<LaydownLogRow | null> {
    return this.dateFriendlyLaydownLogDataSubject;
  }


    /** ############################################################################################################################## 
 * Helper Methods for managing laydown log data within the service.
 * 
 *  /**
   * Extracts occupied spaces from the laydown log list.
   * Populates spaceA and spaceB arrays with job numbers currently occupying each space.
   * Only includes logs without a dateOut value (still actively occupying the space).
   *
   * @param logList The list of laydown logs to process.
   */
  private extractOccupiedLaydownSpaces(logList: ApiLaydownLogRow[]): void {
    const spaceA: string[] = [];
    const spaceB: string[] = [];

    // Loop through logs and categorize by space
    logList.forEach((log) => {
      // Only include logs that don't have a dateOut (still occupying the space)
      if (!log.dateOut && log.jobNumber) {
        if (log.space === 'A') {
          spaceA.push(log.jobNumber);
        } else if (log.space === 'B') {
          spaceB.push(log.jobNumber);
        }
      }
    });

    this.OccupiedLaydownSpaces.next({ spaceA, spaceB });
    console.log('Occupied Spaces:', { spaceA, spaceB });
  }

  /**
   * Checks if a specific job number is currently occupying a specific space.
   * This is used for validation when creating new laydown logs to prevent
   * assigning the same job to the same space multiple times.
   *
   * @param jobNumber The job number to check.
   * @param space The space identifier ('A' or 'B').
   * @returns True if the job number is already occupying the specified space, false otherwise.
   */
  public isSpaceOccupiedByJob(jobNumber: string, space: string): boolean {
    if (!jobNumber || !space) {
      return false;
    }

    const currentOccupiedLaydownSpaces = this.OccupiedLaydownSpaces.value;
    
    if (space === 'A') {
      return currentOccupiedLaydownSpaces.spaceA.includes(jobNumber);
    } else if (space === 'B') {
      return currentOccupiedLaydownSpaces.spaceB.includes(jobNumber);
    }
    
    return false;
  }

  /**
   * Adds a job number to the occupied spaces tracking after a new log is submitted.
   * Updates the OccupiedLaydownSpaces BehaviorSubject to reflect the newly occupied space.
   *
   * @param jobNumber The job number to add.
   * @param space The space identifier ('A' or 'B').
   */
  private addJobToOccupiedLaydownSpaces(jobNumber?: string, space?: string): void {
    if (!jobNumber || !space) {
      return;
    }

    const currentOccupiedLaydownSpaces = this.OccupiedLaydownSpaces.value;
    
    if (space === 'A' && !currentOccupiedLaydownSpaces.spaceA.includes(jobNumber)) {
      currentOccupiedLaydownSpaces.spaceA.push(jobNumber);
    } else if (space === 'B' && !currentOccupiedLaydownSpaces.spaceB.includes(jobNumber)) {
      currentOccupiedLaydownSpaces.spaceB.push(jobNumber);
    }
    
    // Emit updated occupied spaces
    this.OccupiedLaydownSpaces.next({ ...currentOccupiedLaydownSpaces });
    console.log('Updated Occupied Spaces after submission:', currentOccupiedLaydownSpaces);
  }

  /**
   * Updates occupied spaces tracking after a log entry is edited.
   * Handles changes to job number, space, or both by removing the old combination
   * and adding the new combination to the appropriate space arrays.
   *
   * @param originalJobNumber The original job number before edit.
   * @param originalSpace The original space before edit ('A' or 'B').
   * @param newJobNumber The new job number after edit.
   * @param newSpace The new space after edit ('A' or 'B').
   */
  public updateOccupiedSpacesAfterEdit(
    originalJobNumber: string,
    originalSpace: string,
    newJobNumber: string,
    newSpace: string
  ): void {
    if (!originalJobNumber || !originalSpace || !newJobNumber || !newSpace) {
      return;
    }

    const currentOccupiedLaydownSpaces = this.OccupiedLaydownSpaces.value;
    
    // Remove old job number from old space
    if (originalSpace === 'A') {
      const index = currentOccupiedLaydownSpaces.spaceA.indexOf(originalJobNumber);
      if (index > -1) {
        currentOccupiedLaydownSpaces.spaceA.splice(index, 1);
      }
    } else if (originalSpace === 'B') {
      const index = currentOccupiedLaydownSpaces.spaceB.indexOf(originalJobNumber);
      if (index > -1) {
        currentOccupiedLaydownSpaces.spaceB.splice(index, 1);
      }
    }
    
    // Add new job number to new space
    if (newSpace === 'A' && !currentOccupiedLaydownSpaces.spaceA.includes(newJobNumber)) {
      currentOccupiedLaydownSpaces.spaceA.push(newJobNumber);
    } else if (newSpace === 'B' && !currentOccupiedLaydownSpaces.spaceB.includes(newJobNumber)) {
      currentOccupiedLaydownSpaces.spaceB.push(newJobNumber);
    }
    
    // Emit updated occupied spaces
    this.OccupiedLaydownSpaces.next({ ...currentOccupiedLaydownSpaces });
    console.log('Updated Occupied Spaces after edit:', currentOccupiedLaydownSpaces);
  }



  /** ############################################################################################################################## 
 * Methods used by app components to fetch and submit laydown log data. 
 * These methods trigger the corresponding HTTP requests to the backend API for data retrieval and updates.
 
* For each method, the 'dataLoading' BehaviorSubject is updated with loading state information, 
* allowing components to show loading indicators while data is being fetched or submitted.
* If there is an error, a toast alert is shown to the user with an error message.
**/



  /**
   * Fetches the laydown logs based on the search criteria. The `fetchedLaydownLogs` BehaviorSubject is
   * updated with the results, allowing any components subscribed to it to automatically update with
   * the newly fetched logs.
   *
   * When called without parameters (or with storageFacility only), uses default filter parameters
   * and extracts occupied spaces. This is typically used for initial loads or location changes.
   *
   * When called with custom search parameters, uses those parameters without extracting occupied spaces.
   * This is typically used for filtering and pagination in table views.
   *
   * @param LaydownLogSearch Optional search criteria. If omitted, uses default parameters for the current location.
   * @param storageFacility Optional storage facility name. Used when LaydownLogSearch is omitted.
   */
  public fetchLaydownLogs(laydownLogSearch?: LaydownLogSearch, storageFacility?: string): void {
    const isInitialFetch = !laydownLogSearch;
    
    // If no search params provided, create default search with storage facility
    const searchParams = laydownLogSearch || new LaydownLogSearch({
      storageFacility: storageFacility || '',
      filterColumnParams: { ...this.initialFilterColumnParams },
    });
    
    this.dataLoading.next({
      isLoading: true,
      message: this.loadingText.loadingLogs,
    });
    
    this.GetLaydownLogs(searchParams).subscribe({
      next: (results) => {
        this.fetchedLaydownLogs.next(results);
        
        // Only extract occupied spaces for initial fetches (no custom search params)
        if (isInitialFetch) {
          this.extractOccupiedLaydownSpaces(results.logList);
        }
      },

      error: (err: HttpErrorResponse) => {
        console.error(err);
        this._toastService.showWarningMessage(
          this.toastText.logEntry.fetch.error
        );
      },
      complete: () => {
        this.dataLoading.next(this.dataLoadingDefault);
      },
    });
  }

  /**
   * Submits a new laydown log with optional photos. On success, the `fetchedLaydownLogs` BehaviorSubject will be updated
   * if necessary, and components subscribed to it will automatically reflect the changes.
   *
   * This method is called within laydown space form components when the user submits a new log.
   *
   * @param LaydownLogRequest The laydown log data to submit.
   * @param photos Optional collection of photo data to upload with the log.
   */
  public submitLog(
    LaydownLogRequest: ApiLaydownLogRow,
    onSuccessFunction?: Function
  ): void {
    this.dataLoading.next({
      isLoading: true,
      message: this.loadingText.submittingLog,
    });

    // Make the API call
    this.PostLaydownLog(LaydownLogRequest).subscribe({
      next: async (response: any) => {
        // Check for 200 status and process the response message
        if (response && response.message) {
          this._toastService.showSuccessMessage(response.message);
        } else {
          this._toastService.showSuccessMessage(
            this.toastText.logEntry.submit.success
          );
        }
        
        // Add the job number to occupied spaces after successful submission
        this.addJobToOccupiedLaydownSpaces(LaydownLogRequest.jobNumber, LaydownLogRequest.space);
      },
      error: (err: HttpErrorResponse) => {
        console.error(err);
        let errorMessage = this.toastText.logEntry.submit.error;
        if (err.status === 413) {
          errorMessage = this.toastText.photos.processing.sizeError;
        } else if (err.error?.message) {
          errorMessage = err.error.message;
        }
        this._toastService.showWarningMessage(errorMessage);
        this.dataLoading.next(this.dataLoadingDefault);
      },
      complete: () => {
        this.dataLoading.next(this.dataLoadingDefault);
        if (onSuccessFunction) {
          onSuccessFunction();
        }
      },
    });
  }

  /**
   * Updates an existing laydown log. On success, the `fetchedLaydownLogs` BehaviorSubject will be updated
   * with the latest logs if necessary, and subscribed components will automatically reflect the changes.
   *
   * This method is called within laydown space components when the user updates an existing log.
   *
   * @param LaydownLogRow The laydown log data to update.
   * @param onSuccessFunction Optional callback function to execute after successful update.
   */
  public updateLog(
    LaydownLogRow: LaydownLogRow,
    onSuccessFunction?: Function
  ): void {
    this.dataLoading.next({
      isLoading: true,
      message: this.loadingText.updatingLog,
    });
    this.PutLaydownLog(LaydownLogRow).subscribe({
      next: (response: any) => {
        // Check for 200 status and process the response message
        if (response && response.message) {
          this._toastService.showSuccessMessage(response.message);
        } else {
          this._toastService.showSuccessMessage(
            this.toastText.logEntry.update.success
          );
        }
        if (onSuccessFunction) {
          onSuccessFunction();
        }
      },

      error: (err: HttpErrorResponse) => {
        console.error(err);
        this._toastService.showWarningMessage(
          this.toastText.logEntry.update.error
        );
      },
      complete: () => {
        this.dataLoading.next(this.dataLoadingDefault);
      },
    });
  }

  /**
   * Deletes a laydown log by ID. On success, the `fetchedLaydownLogs` BehaviorSubject will be updated
   * with the latest logs if necessary, and subscribed components will automatically reflect the changes.
   *
   * This method is called within laydown space components when the user deletes a log entry.
   *
   * @param logId The ID of the laydown log to delete.
   * @param onSuccessFunction Optional callback function to execute after successful deletion.
   */
  public deleteLog(logId: number, onSuccessFunction?: Function): void {
    this.dataLoading.next({
      isLoading: true,
      message: this.loadingText.deletingLog,
    });

    this.DeleteLaydownLog(logId).subscribe({
      next: (response: any) => {
        // Check for 200 status and process the response message
        if (response && response.message) {
          this._toastService.showSuccessMessage(response.message);
        } else {
          this._toastService.showSuccessMessage(
            this.toastText.logEntry.delete.success
          );
        }
        if (onSuccessFunction) {
          onSuccessFunction();
        }
      },

      error: (err: HttpErrorResponse) => {
        console.error(err);
        this._toastService.showWarningMessage(
          this.toastText.logEntry.delete.error
        );
      },

      complete: () => {
        this.dataLoading.next(this.dataLoadingDefault);
      },
    });
  }

 

  /** ##############################################################################################################################
   * HTTP request functions that make API calls to the backend to perform operations such as fetching, submitting, and updating laydown log data.
   * These functions handle the actual HTTP requests to the backend API,
   * and they acquire an access token from `EntraService` before making the request.
   *
   * The following operations are handled by the HTTP request functions:
   * - Retrieve laydown logs based on search criteria
   * - Submit new laydown logs
   * - Update existing laydown logs
   * - Delete laydown logs
   */

  /**
   * HTTP Request to retrieve laydown logs based on search criteria.
   * Makes a POST request to fetch laydown logs according to the specified search parameters.
   * @param LaydownLogSearch The search criteria for retrieving laydown logs.
   * @returns An observable that resolves to the response containing the laydown logs.
   */

  public GetLaydownLogs(
    LaydownLogSearch: LaydownLogSearch
  ): Observable<GetLaydownLogsResponse> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${environment.apiUrl}/V1/all-laydown-logs`;
        const headers: HttpHeaders = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
        });

        return this._httpClient
          .post<GetLaydownLogsResponse>(url, LaydownLogSearch, {
            headers: headers,
          })
          .pipe(catchError((error) => handleErrorFunction(error)));
      })
    );
  }

  /**
   * HTTP Request to submit a new laydown log 
   * Makes a POST request to add a new laydown log to the backend.
   * @param LaydownLogRequest The laydown log data to be submitted.
   * @returns An observable that resolves to a response object with a message property.
   */
  public PostLaydownLog(
    laydownLogRequest: ApiLaydownLogRow
  ): Observable<{ message: string }> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${environment.apiUrl}/V1/add-laydown-row`;
        const headers: HttpHeaders = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
        });

        console.log('Form Data in PostLaydownLog:');
        return this._httpClient
          .post<{ message: string }>(url, laydownLogRequest, {
            headers: headers,
          })
          .pipe(catchError((error) => handleErrorFunction(error)));
      })
    );
  }

  /**
   * HTTP Request to update an existing laydown log.
   * Makes a PUT request to the backend to update the specified laydown log.
   * @param LaydownLogRow The laydown log data to be updated.
   * @returns An observable that resolves to a response object with a message property.
   */

  public PutLaydownLog(
    LaydownLogRow: LaydownLogRow
  ): Observable<{ message: string }> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const apiLaydownLogRow = new ApiLaydownLogRow(LaydownLogRow);
        const url = `${environment.apiUrl}/V1/update-laydown-row`;
        const headers: HttpHeaders = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
        });

        return this._httpClient
          .put<{ message: string }>(url, apiLaydownLogRow, {
            headers: headers,
          })
          .pipe(catchError((error) => handleErrorFunction(error)));
      })
    );
  }

  /**
   * HTTP Request to delete an existing laydown log.
   * Makes a DELETE request to the backend to delete the laydown log corresponding to the log entry Id.
   * @param logId The Id of the laydown log entry to be deleted.
   * @returns An observable that resolves to a response object with a message property.
   */
  public DeleteLaydownLog(logId: number): Observable<{ message: string }> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${environment.apiUrl}/V1/delete-laydown-row/${logId}`;
        const headers: HttpHeaders = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
        });

        return this._httpClient
          .delete<{ message: string }>(url, { headers: headers })
          .pipe(catchError((error) => handleErrorFunction(error)));
      })
    );
  }
}
